import { Helmet } from "react-helmet";
import BlogArticleLayout from "@/components/BlogArticleLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, MessageSquare, Zap, Settings, CheckCircle2, ArrowRight, Sparkles, Globe, Clock, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import chatgptLogo from "@/assets/blog/chatgpt-logo.svg";
import call24x7Logo from "@/assets/logo.png";

const ChatGPTMCPPhoneCalls = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.chatGPTMCPPhoneCalls.metaTitle')}</title>
        <meta name="description" content={t('blog.chatGPTMCPPhoneCalls.metaDescription')} />
        <meta name="keywords" content="ChatGPT phone calls, MCP integration, Call24x7 MCP, AI phone calls, ChatGPT to phone, Model Context Protocol, AI calling assistant" />
        <link rel="canonical" href="https://call24x7.ai/blog/chatgpt-mcp-phone-calls" />
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/chatgpt-mcp-phone-calls" />
        <meta property="og:title" content={t('blog.chatGPTMCPPhoneCalls.title')} />
        <meta property="og:description" content={t('blog.chatGPTMCPPhoneCalls.metaDescription')} />
        <meta property="og:image" content="https://call24x7.ai/og-chatgpt-mcp.jpg" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.chatGPTMCPPhoneCalls.title'),
            "description": t('blog.chatGPTMCPPhoneCalls.metaDescription'),
            "author": {
              "@type": "Organization",
              "name": "Call24x7.AI"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Call24x7.AI",
              "url": "https://call24x7.ai"
            },
            "datePublished": "2025-12-15",
            "dateModified": "2025-12-15"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.chatGPTMCPPhoneCalls.title')}
        category={t('blog.categories.innovation')}
        date="December 15, 2025"
        readTime={`8 ${t('blog.minRead')}`}
        customHero={
          <div className="bg-gradient-to-br from-primary/10 via-background to-green-500/10 rounded-2xl p-8 md:p-12 border border-border">
            <div className="flex items-center justify-center gap-4 md:gap-8">
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl overflow-hidden bg-[#10a37f] flex items-center justify-center shadow-lg">
                  <img 
                    src={chatgptLogo} 
                    alt="ChatGPT Logo" 
                    className="w-12 h-12 md:w-16 md:h-16"
                  />
                </div>
                <span className="mt-2 text-sm md:text-base font-semibold text-muted-foreground">ChatGPT</span>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 md:w-8 md:h-8 text-primary animate-pulse" />
                </div>
                <span className="mt-2 text-xs md:text-sm font-medium text-primary">MCP</span>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl overflow-hidden bg-primary p-4 shadow-lg">
                  <img 
                    src={call24x7Logo} 
                    alt="Call24x7.AI Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="mt-2 text-sm md:text-base font-semibold text-muted-foreground">Call24x7.AI</span>
              </div>
            </div>
            <p className="text-center mt-6 text-muted-foreground text-sm md:text-base">
              {t('blog.chatGPTMCPPhoneCalls.heroSubtitle')}
            </p>
          </div>
        }
      >
        {/* Introduction */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 rounded-xl mb-8 border border-primary/20">
          <p className="text-lg font-medium text-foreground mb-0">
            <Sparkles className="inline w-5 h-5 mr-2 text-primary" />
            {t('blog.chatGPTMCPPhoneCalls.highlight')}
          </p>
        </div>

        <p className="text-lg mb-6">{t('blog.chatGPTMCPPhoneCalls.intro')}</p>

        {/* What is MCP Section */}
        <h2 className="text-2xl font-bold mt-10 mb-4 flex items-center gap-2">
          <Settings className="w-6 h-6 text-primary" />
          {t('blog.chatGPTMCPPhoneCalls.whatIsMCPTitle')}
        </h2>

        <p className="mb-4">{t('blog.chatGPTMCPPhoneCalls.whatIsMCPDesc')}</p>

        <Card className="mb-8 border-primary/20">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-primary" />
                <h4 className="font-semibold">{t('blog.chatGPTMCPPhoneCalls.step1Label')}</h4>
                <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.step1Example')}</p>
              </div>
              <div className="text-center p-4">
                <Zap className="w-10 h-10 mx-auto mb-2 text-yellow-500" />
                <h4 className="font-semibold">{t('blog.chatGPTMCPPhoneCalls.step2Label')}</h4>
                <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.step2Example')}</p>
              </div>
              <div className="text-center p-4">
                <Phone className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <h4 className="font-semibold">{t('blog.chatGPTMCPPhoneCalls.step3Label')}</h4>
                <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.step3Example')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.chatGPTMCPPhoneCalls.howToConnectTitle')}</h2>

        <div className="space-y-4 mb-8">
          <Card className="border-l-4 border-l-primary">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">1</div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">{t('blog.chatGPTMCPPhoneCalls.connectStep1Title')}</h4>
                  <p className="text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.connectStep1Desc')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-primary">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">2</div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">{t('blog.chatGPTMCPPhoneCalls.connectStep2Title')}</h4>
                  <p className="text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.connectStep2Desc')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-primary">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">3</div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">{t('blog.chatGPTMCPPhoneCalls.connectStep3Title')}</h4>
                  <p className="text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.connectStep3Desc')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Use Cases */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.chatGPTMCPPhoneCalls.useCasesTitle')}</h2>

        <p className="mb-6">{t('blog.chatGPTMCPPhoneCalls.useCasesIntro')}</p>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200/50">
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                {t('blog.chatGPTMCPPhoneCalls.useCase1Title')}
              </h4>
              <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.useCase1Desc')}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20 border-green-200/50">
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                {t('blog.chatGPTMCPPhoneCalls.useCase2Title')}
              </h4>
              <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.useCase2Desc')}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200/50">
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600" />
                {t('blog.chatGPTMCPPhoneCalls.useCase3Title')}
              </h4>
              <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.useCase3Desc')}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-950/30 dark:to-orange-900/20 border-orange-200/50">
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-orange-600" />
                {t('blog.chatGPTMCPPhoneCalls.useCase4Title')}
              </h4>
              <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.useCase4Desc')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Benefits */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.chatGPTMCPPhoneCalls.benefitsTitle')}</h2>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold">{t('blog.chatGPTMCPPhoneCalls.benefit1Title')}</h4>
              <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.benefit1Desc')}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Globe className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold">{t('blog.chatGPTMCPPhoneCalls.benefit2Title')}</h4>
              <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.benefit2Desc')}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold">{t('blog.chatGPTMCPPhoneCalls.benefit3Title')}</h4>
              <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.benefit3Desc')}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold">{t('blog.chatGPTMCPPhoneCalls.benefit4Title')}</h4>
              <p className="text-sm text-muted-foreground">{t('blog.chatGPTMCPPhoneCalls.benefit4Desc')}</p>
            </div>
          </div>
        </div>

        {/* Example Conversation */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.chatGPTMCPPhoneCalls.exampleTitle')}</h2>

        <Card className="mb-8 bg-muted/30">
          <CardContent className="pt-6">
            <div className="space-y-4 font-mono text-sm">
              <div className="flex gap-3">
                <span className="text-blue-500 font-semibold">{t('blog.chatGPTMCPPhoneCalls.you')}:</span>
                <span>{t('blog.chatGPTMCPPhoneCalls.exampleUser')}</span>
              </div>
              <div className="flex gap-3">
                <span className="text-green-500 font-semibold">ChatGPT:</span>
                <span>{t('blog.chatGPTMCPPhoneCalls.exampleAI1')}</span>
              </div>
              <div className="flex gap-3 pl-6 border-l-2 border-primary/30 italic text-muted-foreground">
                <span>{t('blog.chatGPTMCPPhoneCalls.exampleCalling')}</span>
              </div>
              <div className="flex gap-3">
                <span className="text-green-500 font-semibold">ChatGPT:</span>
                <span>{t('blog.chatGPTMCPPhoneCalls.exampleAI2')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Getting Started */}
        <h2 className="text-2xl font-bold mt-10 mb-4">{t('blog.chatGPTMCPPhoneCalls.getStartedTitle')}</h2>

        <p className="mb-6">{t('blog.chatGPTMCPPhoneCalls.getStartedDesc')}</p>

        <Card className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground mb-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.chatGPTMCPPhoneCalls.ctaTitle')}</h3>
            <p className="mb-4 opacity-90">{t('blog.chatGPTMCPPhoneCalls.ctaDesc')}</p>
            <Button 
              variant="secondary" 
              size="lg"
              onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}
              className="gap-2"
            >
              {t('blog.chatGPTMCPPhoneCalls.ctaButton')} <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        <p className="text-muted-foreground text-sm">
          <strong>{t('blog.chatGPTMCPPhoneCalls.note')}:</strong> {t('blog.chatGPTMCPPhoneCalls.noteDesc')}
        </p>
      </BlogArticleLayout>
    </>
  );
};

export default ChatGPTMCPPhoneCalls;
